import React, { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { ApkService, AuthService } from "../../services";

const formatUploadedAt = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
};

const ApkManagement = () => {
  const fileInputRef = useRef(null);
  const [version, setVersion] = useState("");
  const [file, setFile] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const canViewPage = AuthService.canViewApkManagement();

  const loadVersions = () => {
    setLoading(true);
    ApkService.getVersions()
      .then((response) => {
        setVersions(Array.isArray(response?.data) ? response.data : []);
      })
      .catch((error) => {
        window.alert(error?.response?.data?.message || "Failed to load APK versions.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!canViewPage) return;
    loadVersions();
  }, [canViewPage]);

  const onFileChange = (event) => {
    const nextFile = event?.target?.files?.[0] || null;
    setFile(nextFile);
  };

  const onUpload = (event) => {
    event.preventDefault();
    const nextVersion = `${version || ""}`.trim();
    if (!nextVersion) {
      window.alert("Please enter a version number.");
      return;
    }
    if (!file) {
      window.alert("Please choose an APK file to upload.");
      return;
    }
    setUploading(true);
    ApkService.upload(file, nextVersion)
      .then(() => {
        setVersion("");
        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        loadVersions();
      })
      .catch((error) => {
        window.alert(error?.response?.data?.message || "Failed to upload APK.");
      })
      .finally(() => {
        setUploading(false);
      });
  };

  if (!canViewPage) {
    return <Navigate to="/employees/list" replace />;
  }

  return (
    <>
      <div className="list row mb-4">
        <div className="col-md-12 text-primary">
          <h5>APK Management</h5>
        </div>
      </div>
      <div className="list row mb-4">
        <div className="col-md-12">
          <form className="d-flex align-items-end flex-wrap" style={{ gap: "12px" }} onSubmit={onUpload}>
            <label>
              Version
              <input
                className="ms-2"
                type="text"
                value={version}
                placeholder="e.g. 1.02"
                onChange={(e) => setVersion(e.currentTarget.value)}
                disabled={uploading}
              />
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".apk,application/vnd.android.package-archive"
              onChange={onFileChange}
              disabled={uploading}
            />
            <button className="btn btn-primary btn-sm" type="submit" disabled={uploading}>
              {uploading ? "Uploading..." : "Upload APK"}
            </button>
          </form>
        </div>
      </div>
      <div className="list row">
        <div className="col-md-12">
          {loading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <table className="personnel-info-table">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>APK</th>
                  <th>Uploaded At</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {versions.map((item) => (
                  <tr key={item.id}>
                    <td>{item.version}</td>
                    <td>{item.original_name || "current.apk"}</td>
                    <td>{formatUploadedAt(item.created_at)}</td>
                    <td>
                      {item.is_current && item.download_url ? (
                        <a href={item.download_url} target="_blank" rel="noreferrer">
                          Current APK
                        </a>
                      ) : (
                        ""
                      )}
                    </td>
                  </tr>
                ))}
                {versions.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ textAlign: "center" }}>
                      No APK versions uploaded.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
};

export default ApkManagement;
